import { SignupMatchSignal } from '@navio/nsdk';

export type VerifiedSignupInput = {
  firstName: string;
  lastName: string;
  email: string;
  licensePlateNumbers: string[];
};

type VerifiedSignupFormFields = {
  firstName: string;
  surname: string;
  email: string;
  licensePlateNumbers: string[];
};

export type SignupPreviewMatch = {
  signal: SignupMatchSignal;
  matched: boolean;
};

export type ConfirmedSignupPreview = {
  input: VerifiedSignupInput;
  emailMatched: boolean;
  /** Exactly one account holds this email, so a sign-in link can be mailed to it. */
  emailLoginAvailable: boolean;
};

export function toVerifiedSignupInput(value: VerifiedSignupFormFields): VerifiedSignupInput {
  return {
    firstName: value.firstName,
    lastName: value.surname,
    email: value.email,
    licensePlateNumbers: value.licensePlateNumbers.map((plate) => plate.trim()).filter((plate) => plate.length > 0)
  };
}

function samePlateList(left: readonly string[], right: readonly string[]): boolean {
  return left.length === right.length && left.every((plate, index) => plate === right[index]);
}

function sameVerifiedSignupInput(left: VerifiedSignupInput, right: VerifiedSignupInput): boolean {
  return (
    left.firstName === right.firstName &&
    left.lastName === right.lastName &&
    left.email === right.email &&
    samePlateList(left.licensePlateNumbers, right.licensePlateNumbers)
  );
}

function signalMatched(matches: readonly SignupPreviewMatch[], signal: SignupMatchSignal): boolean {
  return matches.some((match) => match.signal === signal && match.matched);
}

export function confirmedSignupPreview(
  input: VerifiedSignupInput,
  matches: readonly SignupPreviewMatch[],
  emailLoginAvailable: boolean
): ConfirmedSignupPreview {
  return {
    input,
    emailMatched: signalMatched(matches, SignupMatchSignal.EMAIL),
    emailLoginAvailable
  };
}

export type VerifiedSignupResolution = { kind: 'preview' } | { kind: 'warn-then-create' } | { kind: 'email-login' };

/**
 * One outcome for a confirmed preview, valid only while the payload still matches.
 *
 * An email that already belongs to an account is never resolved here: reaching
 * that account requires proving the email, which happens over email itself.
 */
export function resolveVerifiedSignup(
  confirmed: ConfirmedSignupPreview | null,
  next: VerifiedSignupInput
): VerifiedSignupResolution {
  if (confirmed === null || !sameVerifiedSignupInput(confirmed.input, next)) {
    return { kind: 'preview' };
  }
  if (confirmed.emailMatched) {
    return { kind: 'email-login' };
  }
  return { kind: 'warn-then-create' };
}

export function previewHasMatch(matches: readonly { matched: boolean }[]): boolean {
  return matches.some((match) => match.matched);
}
